import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ShopApproveWording() {
  const { token } = useParams();
  const [data, setData] = useState(null); // null = loading, false = not found
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/api/shop/approve/${token}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(false); });
    return () => { alive = false; };
  }, [token]);

  const respond = async (approve) => {
    setSubmitting(approve ? "approve" : "reject");
    try {
      const { data: resp } = await api.post(`/api/shop/approve/${token}`, { approve, notes });
      setResult(resp.service_status);
      toast.success(approve ? "Thank you — your approval has been recorded." : "Thanks — we've sent your note to the team.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(null);
    }
  };

  if (data === null) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="shop-approve-loading">Loading…</div>;
  }
  if (data === false) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="shop-approve-not-found">
        <XCircle className="w-12 h-12 mx-auto text-red-600" />
        <h1 className="mt-4 font-display text-2xl font-bold">This link is invalid or has expired.</h1>
        <p className="mt-2 text-ink-muted">Please contact hello@justcarecommunity.com if you need help.</p>
      </div>
    );
  }

  const effectiveStatus = result || data.service_status;

  return (
    <div className="container-jcc py-16 max-w-2xl" data-testid="page-shop-approve">
      <h1 className="font-display text-3xl font-bold">Review your wording</h1>
      <p className="mt-2 text-ink-muted">{data.product_title}</p>

      {effectiveStatus === "approved" && (
        <div className="mt-8 rounded-2xl border border-teal/20 bg-teal/5 p-6 text-center" data-testid="shop-approve-success">
          <CheckCircle2 className="w-10 h-10 mx-auto text-teal" />
          <p className="mt-3 font-medium">Thank you — you've approved this wording.</p>
        </div>
      )}
      {effectiveStatus === "changes_requested" && (
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="shop-approve-changes-sent">
          <p className="font-medium">Thanks — we've shared your note with the team and will send a revised version to review.</p>
        </div>
      )}
      {effectiveStatus === "completed" && (
        <div className="mt-8 rounded-2xl border border-teal/20 bg-teal/5 p-6 text-center" data-testid="shop-approve-completed">
          <CheckCircle2 className="w-10 h-10 mx-auto text-teal" />
          <p className="mt-3 font-medium">This order is already complete.</p>
        </div>
      )}

      {effectiveStatus === "awaiting_approval" && (
        <>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 whitespace-pre-line text-ink" data-testid="shop-approve-wording">
            {data.draft_wording}
          </div>
          <p className="mt-4 text-sm text-ink-muted">Nothing will be added to your JCC account until you approve it below.</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button onClick={() => respond(true)} disabled={!!submitting} className="btn-teal justify-center disabled:opacity-60" data-testid="shop-approve-approve-btn">
              {submitting === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve this wording
            </button>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium mb-1.5">Or request changes</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Tell us what you'd like changed"
              data-testid="shop-approve-notes-input" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            <button onClick={() => respond(false)} disabled={!!submitting || !notes.trim()}
              className="btn-outline mt-3 justify-center disabled:opacity-60" data-testid="shop-approve-reject-btn">
              {submitting === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Request changes
            </button>
          </div>
        </>
      )}
    </div>
  );
}
